import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { Logo } from "@/components/landing/Reveal";
import { api, formatApiError } from "@/lib/api";

export default function AdminLogin() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true);
        setError("");
        try {
            await api.post("/auth/login", { email, password });
            navigate("/itf-control-panel/dashboard");
        } catch (err) {
            setError(formatApiError(err, "Login failed."));
            setLoading(false);
        }
    };

    const inputCls =
        "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10";

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#FAFBFD] px-4" data-testid="admin-login-page">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="w-full max-w-sm"
            >
                <div className="mb-8 flex justify-center">
                    <Link to="/">
                        <Logo />
                    </Link>
                </div>
                <form onSubmit={submit} className="rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm">
                    <h1 className="font-display text-xl font-bold tracking-tight text-slate-900">Owner dashboard</h1>
                    <p className="mt-1.5 text-sm text-slate-500">Sign in to manage orders and TradingView access.</p>
                    <div className="mt-6 space-y-4">
                        <input
                            type="email"
                            data-testid="admin-login-email-input"
                            className={inputCls}
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                        />
                        <input
                            type="password"
                            data-testid="admin-login-password-input"
                            className={inputCls}
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                        />
                    </div>
                    {error && (
                        <p data-testid="admin-login-error" className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-600">
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={loading || !email || !password}
                        data-testid="admin-login-submit-button"
                        className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-slate-800 disabled:opacity-40"
                    >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                        Sign in
                    </button>
                </form>
            </motion.div>
        </div>
    );
}
